import {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useOutlet,
  type Location,
} from 'react-router';
import { motion } from 'motion/react';
import { ArrowLeft, X } from 'lucide-react';

/**
 * Settings and title pages open as a panel over the page you were on (like Discord's settings),
 * instead of replacing it. The page behind stays mounted, frozen at its own address and scroll
 * position, and closing the panel goes back in history to it.
 */
const PANEL_PATH = /^\/(settings(\/.*)?|movie\/[^/]+|series\/[^/]+|downloads|friends|stats)\/?$/;
export const isPanelPath = (pathname: string) => PANEL_PATH.test(pathname);

const InPanelContext = createContext(false);
/** True for pages rendered inside the panel: they sit closer to the top and scroll the panel. */
export const useInPanel = () => useContext(InPanelContext);

interface Background {
  outlet: ReactElement | null;
  location: Location;
  /** The window scroll position when the panel opened; null while the page is in front. */
  scroll: number | null;
  /** Its place in the browser history (React Router's history.state.idx). */
  idx: number | null;
}

const historyIdx = (): number | null => {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : null;
};

export function usePagePanel() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const outlet = useOutlet();
  const navigate = useNavigate();
  const inPanelPath = isPanelPath(location.pathname);

  const background = useRef<Background | null>(null);
  const scroll = useRef(0);
  /** The pages the panel has shown, by history position, for its Back button. */
  const trail = useRef(new Map<number, string>());

  const idx = historyIdx();
  if (!inPanelPath) {
    background.current = { outlet, location, scroll: null, idx };
    trail.current.clear();
  } else {
    if (background.current && background.current.scroll === null)
      background.current.scroll = scroll.current;
    if (idx != null) trail.current.set(idx, location.pathname);
  }
  // Back inside the panel, when the page before it was a different kind of page (Friends, then
  // a film it links to). Moving between Settings pages has the Settings sidebar instead.
  const prev = idx != null ? trail.current.get(idx - 1) : undefined;
  const from = background.current?.idx;
  const backTo =
    inPanelPath &&
    prev &&
    idx != null &&
    from != null &&
    idx - 1 > from &&
    section(prev) !== section(location.pathname)
      ? prev
      : null;

  useEffect(() => {
    if (inPanelPath) return;
    const save = () => (scroll.current = window.scrollY);
    save();
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [inPanelPath]);

  const open = inPanelPath && !!background.current;
  // Straight back to the page behind, however many pages the panel went through. Read from the
  // history itself at the moment of closing, so a quick Escape can't land on a page in between.
  const close = () => {
    const from = background.current?.idx;
    const now = historyIdx();
    if (from != null && now != null && now > from) navigate(from - now);
    else navigate(background.current ? background.current.location : '/home');
  };
  return {
    open,
    outlet,
    background: background.current,
    close,
    navigationType,
    backTo,
    back: () => navigate(-1),
  };
}

/** The kind of panel page: settings, friends, downloads, stats, movie or series. */
const section = (pathname: string) => pathname.split('/')[1] ?? '';

export function PagePanel({
  label,
  onClose,
  back,
  children,
}: {
  label: string;
  onClose: () => void;
  /** A Back button to the page before, inside the panel. */
  back?: { label: string; onBack: () => void } | null;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const scroller = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
  }, [pathname]);

  useEffect(() => {
    dialog.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Leave Escape to menus, pop-ups and dialogs opened inside the panel.
      if (document.querySelector('[role="dialog"]:not(#tp-panel), [role="menu"], [role="listbox"]'))
        return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-y-0 left-0 right-[var(--tp-dock-w,0px)] z-[45] flex items-stretch justify-center sm:items-center sm:p-6 lg:p-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
      />
      <motion.div
        ref={dialog}
        id="tp-panel"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        initial={{ opacity: 0, y: 14, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.98 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        className="relative flex h-full w-full max-w-[1360px] flex-col overflow-hidden bg-app outline-none shadow-[0_40px_120px_-30px_rgba(0,0,0,.95)] sm:h-[min(92dvh,1040px)] sm:rounded-[28px] sm:border sm:border-line"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-20 grid h-10 w-10 place-items-center rounded-full bg-black/55 text-text ring-1 ring-white/15 backdrop-blur transition hover:bg-black/75 hover:ring-pink"
        >
          <X className="h-5 w-5" />
        </button>
        {back && (
          <button
            type="button"
            onClick={back.onBack}
            className="absolute left-4 top-4 z-20 inline-flex h-10 items-center gap-1.5 rounded-full bg-black/55 pl-3 pr-4 text-sm font-semibold text-text ring-1 ring-white/15 backdrop-blur transition hover:bg-black/75 hover:ring-pink"
          >
            <ArrowLeft className="h-4 w-4" /> {back.label}
          </button>
        )}
        <div ref={scroller} className="scrollbar-thin h-full overflow-y-auto overscroll-contain">
          <InPanelContext.Provider value>
            {/* Each page brings its own skeleton (see router.tsx). */}
            <Suspense fallback={null}>{children}</Suspense>
          </InPanelContext.Provider>
        </div>
      </motion.div>
    </motion.div>
  );
}
